import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  PhotoCamera,
  VideoLibrary,
  Star,
} from '@mui/icons-material';
import { propertyApi } from '../services/api';
import { getStorageUrl } from '../config/api';

interface MediaFile {
  path: string;
  url: string;
  type: 'main_image' | 'image' | 'video';
  filename: string;
}

interface PropertyMediaUploadProps {
  propertyId: number;
  onMediaUpdate?: () => void;
}

export const PropertyMediaUpload: React.FC<PropertyMediaUploadProps> = ({
  propertyId,
  onMediaUpdate,
}) => {
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string>('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewMedia, setPreviewMedia] = useState<MediaFile | null>(null);

  const loadMediaFiles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await propertyApi.getMedia(propertyId);
      setMediaFiles(response.media_files || []);
    } catch (err: any) {
      console.error('Error loading media files:', err);
      setError('Failed to load media files');
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  // Load existing media files
  useEffect(() => {
    loadMediaFiles();
  }, [loadMediaFiles]);

  const handleMainImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file for the main image');
      return;
    }

    try {
      setUploading(true);
      setError('');
      await propertyApi.uploadMainImage(propertyId, file);
      await loadMediaFiles();
      onMediaUpdate?.();
    } catch (err: any) {
      console.error('Main image upload error:', err);
      setError(err.response?.data?.message || 'Failed to upload main image');
    } finally {
      setUploading(false);
    }
  };

  const handleMediaUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    // Validate file types
    const validTypes = [
      'image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/svg+xml',
      'video/mp4', 'video/mov', 'video/avi', 'video/wmv', 'video/quicktime', 'video/x-msvideo'
    ];
    const invalidFiles = files.filter(file => !validTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      setError('Some files have invalid formats. Please upload images (JPEG, PNG, GIF, SVG) or videos (MP4, MOV, AVI, WMV)');
      return;
    }

    // Validate file sizes
    const maxImageSize = 10 * 1024 * 1024; // 10MB for images
    const maxVideoSize = 100 * 1024 * 1024; // 100MB for videos
    const oversizedFiles = files.filter(file => {
      const isVideo = file.type.startsWith('video/');
      const maxSize = isVideo ? maxVideoSize : maxImageSize;
      return file.size > maxSize;
    });

          if (oversizedFiles.length > 0) {
        const oversizedFileNames = oversizedFiles.map(f => f.name).join(', ');
        setError(`Some files are too large: ${oversizedFileNames}. Images must be under 10MB, videos under 100MB.`);
        return;
      }

    try {
      setUploading(true);
      setUploadProgress(0);
      setError('');
      
      await propertyApi.uploadMedia(propertyId, files, (progress) => {
        setUploadProgress(progress);
      });
      
      await loadMediaFiles();
      onMediaUpdate?.();
      setUploadProgress(0);
    } catch (err: any) {
      console.error('Media upload error:', err);
      if (err.response?.status === 401) {
        setError('Session expired. Please refresh the page and try again.');
      } else {
        setError(err.response?.data?.message || 'Failed to upload media files. Please check your connection and try again.');
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDeleteMedia = async (filePath: string) => {
    if (!window.confirm('Are you sure you want to delete this media file?')) return;

    try {
      setLoading(true);
      await propertyApi.deleteMedia(propertyId, filePath);
      await loadMediaFiles();
      onMediaUpdate?.();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete media file');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = (media: MediaFile) => {
    setPreviewMedia(media);
    setPreviewOpen(true);
  };

  const renderMediaCard = (media: MediaFile, index: number) => {
    const isVideo = media.type === 'video';
    const isMainImage = media.type === 'main_image';

    return (
      <Grid item xs={12} sm={6} md={4} key={index}>
        <Card 
          sx={{ 
            position: 'relative',
            cursor: 'pointer',
            transition: 'transform 0.2s',
            '&:hover': { transform: 'scale(1.02)' }
          }}
          onClick={() => handlePreview(media)}
        >
          {isMainImage && (
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                left: 8,
                zIndex: 2,
              }}
            >
              <Chip
                icon={<Star />}
                label="Main Image"
                color="primary"
                size="small"
                sx={{ backgroundColor: 'rgba(25, 118, 210, 0.9)', color: 'white' }}
              />
            </Box>
          )}
          
          <IconButton
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
              zIndex: 2,
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteMedia(media.path);
            }}
          >
            <Delete />
          </IconButton>

          {isVideo ? (
            <Box
              sx={{
                height: 200,
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <video
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover' 
                }}
                muted
                preload="metadata"
              >
                <source src={getStorageUrl(media.path)} />
                Your browser does not support the video tag.
              </video>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  left: 8,
                  right: 8,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Chip
                  icon={<VideoLibrary sx={{ fontSize: 16 }} />}
                  label="Video"
                  size="small"
                  sx={{ backgroundColor: 'rgba(0, 0, 0, 0.7)', color: 'white' }}
                />
              </Box>
            </Box>
          ) : (
            <CardMedia
              component="img"
              height="200"
              image={getStorageUrl(media.path)}
              alt={media.filename}
              sx={{ objectFit: 'cover' }}
            />
          )}
          
          <CardContent sx={{ padding: '8px !important' }}>
            <Typography variant="caption" noWrap>
              {media.filename}
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    );
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Property Media
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {uploading && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <Box sx={{ width: '100%', mr: 1 }}>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
            <Box sx={{ minWidth: 35 }}>
              <Typography variant="body2" color="text.secondary">
                {`${uploadProgress}%`}
              </Typography>
            </Box>
          </Box>
          <Typography variant="caption" color="textSecondary">
            {uploadProgress < 100 
              ? `Uploading files... ${uploadProgress}%` 
              : 'Processing files...'
            }
          </Typography>
          <Typography variant="caption" color="primary" display="block" sx={{ mt: 0.5 }}>
            Please don't close this page during upload to prevent session timeout.
          </Typography>
        </Box>
      )}

      {/* Upload Controls */}
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item>
            <Button
              variant="contained"
              component="label"
              startIcon={<Star />}
              disabled={uploading}
              sx={{ mb: 1 }}
            >
              Upload Main Image
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleMainImageUpload}
              />
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUpload />}
              disabled={uploading}
              sx={{ mb: 1 }}
            >
              Upload Images/Videos
              <input
                type="file"
                hidden
                multiple
                accept="image/*,video/*"
                onChange={handleMediaUpload}
              />
            </Button>
          </Grid>
        </Grid>
        
        <Typography variant="caption" color="textSecondary" display="block">
          Supported formats: Images (JPEG, PNG, GIF, SVG) up to 10MB, Videos (MP4, MOV, AVI, WMV) up to 100MB
        </Typography>
      </Box>

      {/* Media Gallery */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : mediaFiles.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 4,
            color: 'text.secondary',
            border: '2px dashed #ccc',
            borderRadius: 2,
          }}
        >
          <PhotoCamera sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h6">No media files yet</Typography>
          <Typography variant="body2">
            Upload a main image and additional photos or videos for this property
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {mediaFiles.map((media, index) => renderMediaCard(media, index))}
        </Grid>
      )}

      {/* Preview Dialog */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {previewMedia?.filename}
          {previewMedia?.type === 'main_image' && (
            <Chip
              icon={<Star />}
              label="Main Image"
              color="primary"
              size="small"
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {previewMedia && (
            <Box sx={{ textAlign: 'center' }}>
              {previewMedia.type === 'video' ? (
                <video
                  controls
                  style={{ maxWidth: '100%', maxHeight: '500px' }}
                  src={getStorageUrl(previewMedia.path)}
                >
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img
                  src={getStorageUrl(previewMedia.path)}
                  alt={previewMedia.filename}
                  style={{ maxWidth: '100%', maxHeight: '500px', objectFit: 'contain' }}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          <Button
            color="error"
            onClick={() => {
              if (previewMedia) {
                handleDeleteMedia(previewMedia.path);
                setPreviewOpen(false);
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
